import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';

import { sql } from '@codemirror/lang-sql';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';
import { useSnackbar } from 'notistack';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AddIcon from '@mui/icons-material/Add';
import AddChartIcon from '@mui/icons-material/Addchart';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_WORKS } from '~/components/brand/brandTokens';

import { extractTitleFromSql } from '~/pages/LeLens/cardTitle';
import { formatLensDayLabel } from '~/pages/LeLens/lensChart';
import { canEditLens } from '~/pages/LeLens/lensLibrary';

import {
  useCreateReportingLeLens,
  useGetReportingLeLenses,
  useUpdateReportingLeLens,
} from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useDryRunReportingLeLensDataset } from '~/hooks/apis/reporting/useLeLensDatasetChecks';
import { lensWriteFailureMessage } from '~/hooks/apis/reporting/useUpdateReportingLeLensErrors';
import useCanAccessLeLens from '~/hooks/useCanAccessLeLens';
import useCurrentUser from '~/hooks/useCurrentUser';

import { type Lens, type LensCard, type LensCardType, type LensDataset } from '~/types/leLens';

import RichardMonogram from './RichardMonogram';
import { isExecutableLensSql } from './lensSql';
import { MONO_FONT_STACK } from './uiTokens';

export { isExecutableLensSql } from './lensSql';

const LazyCodeMirror = lazy(() => import('@uiw/react-codemirror'));

const CODE_FONT_SIZE_PX = 13;
const LINE_HEIGHT_PX = 18;
const PADDING_PX = 24;
const HEADER_HEIGHT_PX = 32;
const FADE_HEIGHT_PX = 24;
const COLLAPSE_THRESHOLD_LINES = 15;
const COLLAPSED_LINES = 8;
const MAX_HEIGHT_PX = 480;

// CodeMirror github themes paint their own editor background; the collapsed fade
// must blend into that exact colour rather than the MUI surface behind it.
const EDITOR_BG_LIGHT = '#ffffff';
const EDITOR_BG_DARK = '#0d1117';

type SqlViewerErrorBoundaryProps = {
  children: React.ReactNode;
  value: string;
};

type SqlViewerErrorBoundaryState = { failed: boolean };

/**
 * CodeMirror is an optional enhancement. Its chunk can fail independently of
 * the answer that contains it, so keep that failure inside this SQL block.
 */
export class SqlViewerErrorBoundary extends React.Component<SqlViewerErrorBoundaryProps, SqlViewerErrorBoundaryState> {
  state: SqlViewerErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): SqlViewerErrorBoundaryState {
    return { failed: true };
  }

  componentDidUpdate(previous: SqlViewerErrorBoundaryProps): void {
    if (this.state.failed && previous.value !== this.props.value) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
            The SQL editor could not load. You can still copy or save this result.
          </Typography>
          <Box
            component="pre"
            data-testid="sql-viewer-fallback"
            sx={{
              fontFamily: MONO_FONT_STACK,
              fontSize: `${CODE_FONT_SIZE_PX}px`,
              lineHeight: `${LINE_HEIGHT_PX}px`,
              m: 0,
              maxHeight: MAX_HEIGHT_PX,
              overflow: 'auto',
              whiteSpace: 'pre',
            }}
          >
            {this.props.value}
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * A card must be backed by a complete query, rather than a select-list
 * expression that happened to appear in an answer or an evidence payload.
 * This is deliberately a light affordance guard, not a SQL parser: the dry
 * run still owns BigQuery validation once a query reaches the save dialog.
 */
/** Keep parser internals out of the reader-facing save decision. */
export function lensDryRunFailureMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : '';
  if (/syntax error|unexpected (?:identifier|keyword|token)|expected .* but got/i.test(raw)) {
    return 'This result is not backed by a complete query yet. Ask askRichard to prepare one before adding it to a Lens.';
  }
  return raw || 'This query could not be checked right now. Close this window and try again.';
}

type Props = {
  value: string;
  /** Embedded-context only (e.g. the unified shell); absent everywhere else, including /ask-richard. */
  onKeepLive?: () => void;
  /** A Lens-scoped conversation makes the save affordance explicit rather than
   * asking an editor to discover a small icon in the SQL toolbar. */
  prominentLensSave?: boolean;
  /** Called after an embedded SQL block is saved into a dashboard. */
  onSavedLens?: (lens: Lens) => void;
};

export default function SqlCodeMirrorBlock({ value, onKeepLive, prominentLensSave = false, onSavedLens }: Props) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const extensions = useMemo(() => [sql()], []);

  const [copyCount, setCopyCount] = useState(0);
  const lineCount = Math.max(3, value.split('\n').length);
  const isCollapsible = lineCount > COLLAPSE_THRESHOLD_LINES;
  const [isExpanded, setIsExpanded] = useState(!isCollapsible);

  // Sync expansion state when content changes (component may be reused with different value)
  useEffect(() => {
    setIsExpanded(!isCollapsible);
  }, [isCollapsible, value]);

  const displayLines = isExpanded ? lineCount : Math.min(lineCount, COLLAPSED_LINES);
  const heightPx = Math.min(MAX_HEIGHT_PX, displayLines * LINE_HEIGHT_PX + PADDING_PX);
  const hiddenLineCount = Math.max(0, lineCount - COLLAPSED_LINES);
  const showFade = isCollapsible && !isExpanded;
  const editorBg = isDarkMode ? EDITOR_BG_DARK : EDITOR_BG_LIGHT;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyCount((c) => c + 1);
    } catch {
      // Clipboard API may fail in some contexts; fail silently
    }
  };

  // Show copied feedback for 2s after each copy; counter ensures repeated clicks restart timer
  const copied = copyCount > 0;
  useEffect(() => {
    if (copyCount === 0) {
      return;
    }
    const timer = setTimeout(() => setCopyCount(0), 2000);
    return () => clearTimeout(timer);
  }, [copyCount]);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: HEADER_HEIGHT_PX,
          px: 1,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: MONO_FONT_STACK,
              fontSize: 12,
              fontWeight: 500,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {`SQL · ${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`}
          </Typography>
          <Box
            component="span"
            sx={{
              fontFamily: MONO_FONT_STACK,
              fontSize: 11,
              lineHeight: 1.4,
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 0.75,
              whiteSpace: 'nowrap',
            }}
          >
            BigQuery
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Tooltip title={copied ? 'Copied' : 'Copy SQL'}>
            <IconButton size="small" onClick={handleCopy} aria-label="Copy SQL to clipboard">
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <SaveSqlToLensButton
            onKeepLive={onKeepLive}
            onSavedLens={onSavedLens}
            sql={value}
            variant={prominentLensSave ? 'button' : 'icon'}
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          '& .le-data-sql-editor .cm-editor': {
            fontSize: `${CODE_FONT_SIZE_PX}px`,
          },
          '& .le-data-sql-editor .cm-line': {
            lineHeight: `${LINE_HEIGHT_PX}px`,
          },
          '& .le-data-sql-editor .cm-gutters': {
            fontSize: `${CODE_FONT_SIZE_PX}px`,
          },
        }}
      >
        <SqlViewerErrorBoundary value={value}>
          <Suspense
            fallback={
              <Typography variant="body2" sx={{ p: 1 }}>
                Loading SQL editor…
              </Typography>
            }
          >
            <LazyCodeMirror
              value={value}
              height={`${heightPx}px`}
              theme={isDarkMode ? githubDark : githubLight}
              extensions={extensions}
              readOnly
              basicSetup={{ lineNumbers: true, foldGutter: false }}
              style={{
                fontSize: `${CODE_FONT_SIZE_PX}px`,
              }}
              className="le-data-sql-editor"
            />
          </Suspense>
        </SqlViewerErrorBoundary>
        {showFade && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: FADE_HEIGHT_PX,
              pointerEvents: 'none',
              background: `linear-gradient(to bottom, rgba(0,0,0,0), ${editorBg})`,
            }}
          />
        )}
      </Box>

      {isCollapsible && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            textAlign: 'center',
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={handleToggleExpand}
            aria-expanded={isExpanded}
            sx={{ fontSize: 12, fontWeight: 500, textTransform: 'none', py: 0.25 }}
          >
            {isExpanded ? 'Show less' : `Show ${hiddenLineCount} more ${hiddenLineCount === 1 ? 'line' : 'lines'}`}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export function SaveSqlToLensButton({
  sql,
  variant = 'button',
  preview,
  initialReplaceMode,
  preferredLensId,
  replaceCard,
  onKeepLive,
  onSavedLens,
  accentColor,
  answerTitle,
  sourceQuestion,
  actionLabel,
  resultRows,
}: {
  sql: string;
  variant?: 'button' | 'icon';
  /** The answer's live visual, carried into the save decision without rerunning the query. */
  preview?: React.ReactNode | ((title: string) => React.ReactNode);
  /** Lets the answer card expose Add as new vs Replace before the dialog opens. */
  initialReplaceMode?: boolean;
  /** The Lens currently in context, used as the preselected destination. */
  preferredLensId?: string;
  /** An existing card this answer explicitly corrects or supersedes. */
  replaceCard?: { id: string; title: string };
  /** Embedded-context only (e.g. the unified shell): routes the click into the shell's own
   *  flow instead of opening the LE Lens dialog. Absent everywhere else, including /ask-richard. */
  onKeepLive?: () => void;
  /** Called after the server confirms the dashboard change. */
  onSavedLens?: (lens: Lens) => void;
  /** Unified analytics surfaces use their shared dark-teal action colour. */
  accentColor?: string;
  /** The answer's own chart or table title, the card's default name. */
  answerTitle?: string;
  /** The question that produced this result, shown as provenance in the save review. */
  sourceQuestion?: string;
  /** Plain, context-specific wording for the visible single-result action. */
  actionLabel?: string;
  /** True result size from askRichard, used to disclose Lens's interactive row cap. */
  resultRows?: number;
}) {
  const canAccessLens = useCanAccessLeLens();
  const [open, setOpen] = useState(false);

  if (!canAccessLens || !isExecutableLensSql(sql)) {
    return null;
  }

  const handleClick = () => {
    if (onKeepLive) {
      onKeepLive();
      return;
    }
    setOpen(true);
  };

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title="Add to Lens">
          <IconButton
            size="small"
            onClick={handleClick}
            aria-label="Add result to Lens"
            sx={accentColor ? { color: accentColor } : undefined}
          >
            <AddChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          onClick={handleClick}
          size="small"
          startIcon={<AddChartIcon />}
          sx={accentColor ? { color: accentColor } : undefined}
        >
          {replaceCard
            ? `Replace ${replaceCard.title}`
            : (actionLabel ?? (preferredLensId ? 'Add to this Lens' : 'Add to Lens'))}
        </Button>
      )}
      {/* Mounted only while open: SaveSqlToLensDialog calls the lens list query and both
          mutation hooks as soon as it exists, so keeping it out of the tree until the user
          actually opens it keeps every other rendered SQL block free of that network cost. */}
      {open && (
        <SaveSqlToLensDialog
          accentColor={accentColor}
          answerTitle={answerTitle}
          preview={preview}
          initialReplaceMode={initialReplaceMode}
          onClose={() => setOpen(false)}
          open={open}
          preferredLensId={preferredLensId}
          replaceCard={replaceCard}
          sql={sql}
          onSavedLens={onSavedLens}
          sourceQuestion={sourceQuestion}
          resultRows={resultRows}
        />
      )}
    </>
  );
}

export function SaveSqlToLensDialog({
  sql,
  open,
  onClose,
  preview,
  initialReplaceMode,
  preferredLensId,
  targetLensOnly = false,
  replaceCard,
  onSavedLens,
  accentColor,
  answerTitle,
  sourceQuestion,
  resultRows,
}: {
  sql: string;
  open: boolean;
  onClose: () => void;
  preview?: React.ReactNode | ((title: string) => React.ReactNode);
  initialReplaceMode?: boolean;
  preferredLensId?: string;
  targetLensOnly?: boolean;
  replaceCard?: { id: string; title: string };
  onSavedLens?: (lens: Lens) => void;
  accentColor?: string;
  answerTitle?: string;
  sourceQuestion?: string;
  resultRows?: number;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const { closeSnackbar } = useSnackbar();
  const { data: lenses = [], isError: lensListFailed, isLoading, refetch: refetchLenses } = useGetReportingLeLenses();
  const { user } = useCurrentUser();
  const editableLenses = useMemo(() => lenses.filter((lens) => canEditLens(lens)), [lenses]);
  const updateLens = useUpdateReportingLeLens();
  const createLens = useCreateReportingLeLens();
  const dryRun = useDryRunReportingLeLensDataset();
  const [lensId, setLensId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState(() => answerTitle?.trim() || titleFromQuestion(sourceQuestion) || 'Answer');
  const [newLensTitle, setNewLensTitle] = useState(`${title} Lens`);
  const [replaceMode, setReplaceMode] = useState(initialReplaceMode ?? Boolean(replaceCard));
  const [showPreview, setShowPreview] = useState(false);
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  const previewContent = typeof preview === 'function' ? preview(title.trim() || 'Answer') : preview;

  // SnackbarProvider sits above AuthRouter, so a React Router Link here has no
  // router context when notistack renders the action. A regular href keeps the
  // toast usable and lets the app router handle the destination after reload.
  const viewLensAction = (lens: { id: string }) => () => (
    <Button color="inherit" href={`/le-lens/${lens.id}`} size="small">
      View Lens
    </Button>
  );

  useEffect(() => {
    dryRun.mutate(sql);
    // Only the SQL itself changes what a dry run reports; re-firing on every render (the
    // mutation object is a new reference each time) would spend a dry run per keystroke
    // elsewhere in the tree.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sql]);

  useEffect(() => {
    if (!open || lensId || isCreating) {
      return;
    }
    if (preferredLensId && editableLenses.some((lens) => lens.id === preferredLensId)) {
      setLensId(preferredLensId);
    }
  }, [editableLenses, lensId, open, preferredLensId]);

  // A reader with no dashboards at all previously met a disabled primary button and had to
  // find the quiet "Create new" text link to get anywhere. Land them on the create form
  // instead. Guarded on the list having actually loaded: an unreachable list also returns
  // zero dashboards, and offering to create a first one there would state something false.
  useEffect(() => {
    if (
      !open ||
      targetLensOnly ||
      isLoading ||
      lensListFailed ||
      lensId ||
      isCreating ||
      (preferredLensId && editableLenses.some((lens) => lens.id === preferredLensId))
    ) {
      return;
    }
    // A current dashboard is an explicit, credible destination. Do not quietly pick the
    // first unrelated dashboard from a list; begin with a new dashboard instead.
    setIsCreating(true);
  }, [editableLenses, isCreating, isLoading, lensId, lensListFailed, open, preferredLensId, targetLensOnly]);

  const save = async () => {
    const cardTitle = title.trim();
    if (!cardTitle || !dryRun.isSuccess) {
      return;
    }
    setSaveFailure(null);
    try {
      const lens = isCreating
        ? undefined
        : editableLenses.find((item) => item.id === (targetLensOnly ? preferredLensId : lensId));
      const dataset: LensDataset = {
        id: uniqueCardId(
          cardTitle,
          (lens?.spec.datasets ?? []).map((entry) => entry.id),
        ),
        name: cardTitle,
        sql,
        columns: dryRun.data.columns,
        source: { kind: 'sql' },
      };
      const answerCard = {
        title: cardTitle,
        type: 'table' as const,
        datasetId: dataset.id,
        view: { mode: 'grouped' as const },
      };

      if (isCreating) {
        if (!newLensTitle.trim()) {
          return;
        }
        const { lens: created } = await createLens.mutateAsync({
          quietFailure: true,
          title: newLensTitle.trim(),
          description: null,
          spec: { version: 1, datasets: [dataset], cards: [{ id: uniqueCardId(cardTitle, []), ...answerCard }] },
          successAction: onSavedLens ? undefined : viewLensAction,
        });
        onSavedLens?.(created);
        onClose();
        return;
      }

      if (!lens) {
        // The chosen dashboard is no longer in the list the dialog is showing: it was
        // deleted, unshared, or the list was reloaded after a conflict. Returning
        // quietly made the primary button look broken, so say what to do instead,
        // in the same place as every other failure this dialog reports.
        setSaveFailure(
          targetLensOnly
            ? 'This Lens is no longer available for editing.'
            : 'That Lens is no longer available. Choose another Lens from the list.',
        );
        return;
      }
      const datasets = [...(lens.spec.datasets ?? []), dataset];

      if (replaceMode && replaceCard && lens.id === preferredLensId) {
        const previousLens = lens;
        const cards = lens.spec.cards.map((card) =>
          card.id === replaceCard.id ? replaceLensCardWithDataset(card, cardTitle, dataset.id) : card,
        );
        const result = await updateLens.mutateAsync({
          quietFailure: true,
          id: lens.id,
          title: lens.title,
          description: lens.description,
          spec: { ...lens.spec, cards, datasets },
          version: lens.version,
          operationsBase: lens,
          successMessage: `Replaced “${replaceCard.title}” on “${lens.title}”.`,
          successAction: (savedLens) => (key) => (
            <Button
              color="inherit"
              onClick={() => {
                closeSnackbar(key);
                void updateLens
                  .mutateAsync({
                    id: previousLens.id,
                    title: previousLens.title,
                    description: previousLens.description,
                    spec: previousLens.spec,
                    version: savedLens.version,
                    successMessage: 'Replacement undone',
                  })
                  .then(({ lens: restored }) => onSavedLens?.(restored));
              }}
              size="small"
            >
              Undo
            </Button>
          ),
        });
        onSavedLens?.(result.lens);
        onClose();
        return;
      }

      const result = await updateLens.mutateAsync({
        quietFailure: true,
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: {
          ...lens.spec,
          cards: [
            ...lens.spec.cards,
            {
              id: uniqueCardId(
                cardTitle,
                lens.spec.cards.map((item) => item.id),
              ),
              ...answerCard,
            },
          ],
          datasets,
        },
        version: lens.version,
        operationsBase: lens,
        successMessage: `Added to “${lens.title}”.`,
        successAction: onSavedLens ? undefined : viewLensAction,
      });
      onSavedLens?.(result.lens);
      onClose();
    } catch (error) {
      // The dialog stays open holding the destination, title, visual and SQL, and states
      // the failure itself. A toast would be the wrong place: it can sit over this
      // dialog's own primary action, so the message telling the reader to try again is
      // what stops them clicking the button that would.
      setSaveFailure(lensWriteFailureMessage(error, isCreating ? 'create' : replaceMode ? 'save' : 'add'));
    }
  };

  const isSaving = updateLens.isPending || createLens.isPending;
  const selectedLens = editableLenses.find((lens) => lens.id === (targetLensOnly ? preferredLensId : lensId)) ?? null;

  return (
    <Dialog
      aria-labelledby="save-to-lens-title"
      PaperProps={{
        sx: {
          backgroundColor: tokens.surface,
          border: `1px solid ${alpha(tokens.accent, 0.16)}`,
          borderRadius: 1.5,
          boxShadow: `0 20px 56px ${alpha(tokens.accent, 0.2)}`,
          maxWidth: targetLensOnly ? 440 : 620,
          overflow: 'hidden',
        },
      }}
      fullWidth
      maxWidth="md"
      onClose={isSaving ? undefined : onClose}
      open={open}
    >
      <Box
        sx={{
          color: tokens.accent,
          backgroundColor: alpha(tokens.accent, 0.05),
          borderBottom: `1px solid ${alpha(tokens.accent, 0.12)}`,
          px: 3,
          py: 2.5,
        }}
      >
        <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <RichardMonogram
              aria-hidden="true"
              inverted={theme.palette.mode === 'dark'}
              sx={{ fontSize: 36, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                id="save-to-lens-title"
                sx={{ fontFamily: FONT_STACK_SUISSE_WORKS, fontSize: 22, lineHeight: 1.3 }}
              >
                {replaceMode && replaceCard ? 'Replace card' : targetLensOnly ? 'Add to this Lens' : 'Add to Lens'}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, overflowWrap: 'anywhere' }} variant="body2">
                {replaceMode && replaceCard
                  ? `Update “${replaceCard.title}” with this answer.`
                  : targetLensOnly
                    ? selectedLens?.title
                    : 'Keep this answer in a Lens.'}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Close"
            disabled={isSaving}
            onClick={onClose}
            size="small"
            sx={{ color: tokens.accent }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
        <Stack spacing={2}>
          {saveFailure ? (
            <Alert onClose={() => setSaveFailure(null)} severity="warning">
              {saveFailure}
            </Alert>
          ) : null}
          {typeof resultRows === 'number' && resultRows > 10_000 ? (
            <Alert severity="warning">
              This answer contains {resultRows.toLocaleString('en-AU')} rows. The Lens will load up to 10,000 rows for
              interactive viewing, and its CSV download will contain those loaded rows. Download the full result from
              this answer when you need every row.
            </Alert>
          ) : null}
          {!saveFailure && (dryRun.isIdle || dryRun.isPending) ? <PinCheckingLine /> : null}
          {!saveFailure && dryRun.isError ? (
            <Alert severity="warning">{lensDryRunFailureMessage(dryRun.error)}</Alert>
          ) : null}
          <Box
            sx={{
              display: targetLensOnly ? 'none' : undefined,
              backgroundColor: alpha(tokens.accent, 0.035),
              borderLeft: `2px solid ${alpha(tokens.accent, 0.5)}`,
              borderRadius: 0.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography color={tokens.accent} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em' }}>
              YOUR ANSWER
            </Typography>
            <Typography
              color={tokens.accent}
              sx={{
                mt: 0.75,
                fontWeight: 600,
                overflowWrap: 'anywhere',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              title={sourceQuestion?.trim() || undefined}
              variant="body2"
            >
              {sourceQuestion?.trim() || title.trim()}
            </Typography>
            <Stack alignItems="flex-start" direction="row" spacing={0.75} sx={{ mt: 1, color: 'text.secondary' }}>
              <CheckIcon sx={{ fontSize: 16, mt: 0.25 }} />
              <Typography variant="caption">
                {typeof resultRows === 'number' && resultRows > 10_000
                  ? 'First 10,000 rows and source query included.'
                  : 'Result and source query included.'}
              </Typography>
            </Stack>
          </Box>
          <TextField
            fullWidth
            label="Card title"
            autoFocus={targetLensOnly}
            onChange={(event) => setTitle(event.target.value)}
            size="small"
            value={title}
          />
          <Box>
            <Typography
              color={tokens.accent}
              fontWeight={600}
              sx={{ mb: 1, display: targetLensOnly ? 'none' : undefined }}
              variant="subtitle2"
            >
              Save to
            </Typography>
            <Box
              aria-label="Lens destination"
              role="group"
              sx={{
                display: targetLensOnly ? 'none' : 'grid',
                gap: 0.5,
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                backgroundColor: tokens.canvas,
                border: `1px solid ${alpha(tokens.accent, 0.12)}`,
                borderRadius: 1,
                p: 0.5,
                mb: 2,
              }}
            >
              <Button
                aria-pressed={!isCreating}
                disabled={isSaving || lensListFailed || (!isLoading && !editableLenses.length)}
                onClick={() => {
                  setIsCreating(false);
                  if (!lensId) {
                    setLensId(
                      preferredLensId && editableLenses.some((lens) => lens.id === preferredLensId)
                        ? preferredLensId
                        : (editableLenses[0]?.id ?? ''),
                    );
                  }
                }}
                variant="text"
                sx={
                  !isCreating
                    ? {
                        backgroundColor: tokens.accent,
                        color: tokens.accentContrast,
                        fontWeight: 700,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: tokens.accent },
                      }
                    : { color: 'text.secondary' }
                }
              >
                Existing Lens
              </Button>
              <Button
                aria-pressed={isCreating}
                disabled={isSaving}
                onClick={() => {
                  setIsCreating(true);
                  setLensId('');
                }}
                startIcon={<AddIcon fontSize="small" />}
                variant="text"
                sx={
                  isCreating
                    ? {
                        backgroundColor: tokens.accent,
                        color: tokens.accentContrast,
                        fontWeight: 700,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: tokens.accent },
                      }
                    : { color: 'text.secondary' }
                }
              >
                New Lens
              </Button>
            </Box>
            {/* An unreachable list returns an empty array, which the picker below renders
                as "No dashboard found". That is a different statement from the truth, so
                the failure is named and given its own retry before the picker is shown. */}
            {lensListFailed ? (
              <Alert
                action={
                  <Button color="inherit" onClick={() => void refetchLenses()} size="small">
                    Try again
                  </Button>
                }
                severity="warning"
                sx={{ mb: 1 }}
              >
                {targetLensOnly
                  ? 'Your Lenses could not be loaded, so this card cannot be added to this Lens yet.'
                  : 'Your Lenses could not be loaded, so this card cannot be added to an existing one yet. You can still create a new Lens for it.'}
              </Alert>
            ) : null}
            {!isCreating && !targetLensOnly ? (
              <Autocomplete
                autoHighlight
                disabled={lensListFailed}
                fullWidth
                getOptionLabel={(lens) => lens.title}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoading}
                loadingText="Loading Lenses…"
                noOptionsText="No Lens found"
                onChange={(_event, lens) => {
                  setLensId(lens?.id ?? '');
                  setIsCreating(false);
                }}
                options={editableLenses}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Find a Lens"
                    placeholder="Search by Lens name"
                    size="small"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon color="action" fontSize="small" sx={{ ml: 0.25, mr: 0.75 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
                renderOption={(props, lens) => (
                  <Box component="li" {...props} key={lens.id} sx={{ py: 1 }}>
                    <Stack
                      alignItems="center"
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ minWidth: 0, width: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 600 }} variant="body2">
                          {lens.title}
                        </Typography>
                        <Tooltip title={`Updated ${formatLensDayLabel(lens.updatedAt, true)}`}>
                          <Typography color="text.secondary" variant="caption">
                            Updated {formatLensDayLabel(lens.updatedAt)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </Box>
                )}
                value={selectedLens}
              />
            ) : null}
            {targetLensOnly && !isLoading && !lensListFailed && !selectedLens ? (
              <Alert severity="warning">You need edit access to add to this Lens.</Alert>
            ) : null}
            {selectedLens && !isCreating && !targetLensOnly ? (
              <Typography
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75, overflowWrap: 'anywhere' }}
                variant="caption"
              >
                {replaceMode && replaceCard
                  ? `This card will replace “${replaceCard.title}” in ${selectedLens.title}.`
                  : `This card will be added to ${selectedLens.title}.`}
              </Typography>
            ) : null}
            {isCreating && (
              <Stack spacing={0.75}>
                <TextField
                  fullWidth
                  label="Lens name"
                  onChange={(event) => setNewLensTitle(event.target.value)}
                  size="small"
                  value={newLensTitle}
                />
                <Typography color="text.secondary" variant="caption">
                  Start with this card and add more answers later.
                </Typography>
              </Stack>
            )}
          </Box>
          {previewContent && !targetLensOnly ? (
            <Box>
              <Button
                aria-expanded={showPreview}
                onClick={() => setShowPreview((current) => !current)}
                size="small"
                sx={{ px: 0 }}
              >
                {showPreview ? 'Hide data preview' : 'Preview data'}
              </Button>
              <Collapse in={showPreview} timeout={0} unmountOnExit>
                <Box
                  aria-label="Card data preview"
                  sx={{
                    minWidth: 0,
                    maxHeight: 240,
                    overflow: 'auto',
                    mt: 1,
                    border: `1px solid ${alpha(tokens.accent, 0.12)}`,
                    borderRadius: 1,
                  }}
                >
                  {previewContent}
                </Box>
              </Collapse>
            </Box>
          ) : null}
          {replaceCard ? (
            <Button
              onClick={() => setReplaceMode((current) => !current)}
              size="small"
              sx={{ alignSelf: 'flex-start', px: 0 }}
            >
              {replaceMode ? 'Add as a new card instead' : `Replace “${replaceCard.title}” instead`}
            </Button>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: tokens.surface,
          borderTop: `1px solid ${alpha(tokens.accent, 0.1)}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Button disabled={isSaving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={
            isLoading ||
            !title.trim() ||
            (isCreating && !newLensTitle.trim()) ||
            (!isCreating && (!lensId || lensListFailed)) ||
            (targetLensOnly && !selectedLens) ||
            isSaving ||
            !dryRun.isSuccess
          }
          onClick={save}
          disableElevation
          sx={
            accentColor
              ? {
                  '&:hover': { bgcolor: accentColor },
                  bgcolor: accentColor,
                  color: tokens.accentContrast,
                }
              : { backgroundColor: tokens.accent, color: tokens.accentContrast }
          }
          variant="contained"
        >
          {isSaving
            ? 'Saving…'
            : isCreating
              ? 'Create Lens'
              : replaceMode && replaceCard
                ? 'Replace card'
                : targetLensOnly
                  ? 'Add'
                  : 'Add to Lens'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Shown while the dry run this dialog fired on open has not settled yet: nothing about the
 *  destination can be decided before it does. */
function PinCheckingLine() {
  return (
    <Alert icon={<CircularProgress size={16} />} severity="info">
      Checking this result…
    </Alert>
  );
}

/** A useful starting card without pretending SQL can always reveal intent perfectly. */
export function inferLensCardFromSql(sqlText: string): { title: string; type: LensCardType } {
  const named = extractTitleFromSql(sqlText);
  const sql = named.sql;
  const selectClause = sql.match(/\bselect\s+([\s\S]*?)\s+\bfrom\b/i)?.[1] ?? '';
  const aliases = [...selectClause.matchAll(/\bas\s+[`"]?([a-z][a-z0-9_]*)[`"]?/gi)].map((match) => match[1]);
  const alias = [...aliases].reverse().find((candidate) => !/^(date|day|week|month|year|period)$/i.test(candidate));
  const title = named.title ?? (alias ? titleFromSqlAlias(alias) : 'New Lens card');
  const hasAggregate = /\b(count|countif|sum|avg|average|min|max)\s*\(/i.test(selectClause);
  const hasGrouping = /\bgroup\s+by\b/i.test(sql);
  const hasTimeDimension =
    /\b(date|timestamp|datetime|day|week|month|quarter|year)\b|(?:^|_)(date|at|day|week|month|year)(?:$|_)/i.test(
      selectClause,
    );

  const groupByColumns = (
    sql.match(/\bgroup\s+by\b([\s\S]*?)(?:\bhaving\b|\border\s+by\b|\blimit\b|\bqualify\b|$)/i)?.[1] ?? ''
  )
    .split(',')
    .filter((part) => part.trim()).length;
  const aggregateCount = (selectClause.match(/\b(count|countif|sum|avg|average|min|max)\s*\(/gi) ?? []).length;
  const readsAsTable = groupByColumns >= 2 || aggregateCount >= 3;

  if (hasAggregate && !hasGrouping) {
    return { title, type: 'metric' };
  }
  if (hasGrouping && readsAsTable) {
    return { title, type: 'table' };
  }
  if (hasGrouping && hasTimeDimension) {
    return { title, type: 'line' };
  }
  if (hasGrouping && hasAggregate) {
    return { title, type: 'bar' };
  }
  return { title, type: 'table' };
}

function titleFromSqlAlias(alias: string): string {
  return alias
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => (word.length <= 3 ? word.toUpperCase() : `${word.charAt(0).toUpperCase()}${word.slice(1)}`))
    .join(' ');
}

/** A replacement that remains on a shared dataset must remove the own query. */
export function replaceLensCardWithDataset(card: LensCard, title: string, datasetId: string): LensCard {
  const { config: _config, ...rest } = card;
  return { ...rest, title, datasetId, view: { mode: 'grouped' } };
}

/** The question an answer was asked, read as a card title rather than as an instruction. */
function titleFromQuestion(question?: string): string | undefined {
  const sentence = question
    ?.split(/(?<=[.?!])\s/)[0]
    ?.replace(/[.?!]+$/, '')
    .trim();
  const subject = sentence
    ?.replace(/^(?:(?:can|could) you |please |give me |show me |show |list |get me |i want |i need )+/i, '')
    .replace(/^(?:a |an |the )?(?:table|chart|list|graph|breakdown)(?: with| of| showing| for)?\s+/i, '')
    .replace(/^(?:the |a |an )/i, '')
    .replace(/\s+as an? (?:\w+ )?(?:chart|table|graph)$/i, '')
    .trim();
  return subject ? `${subject.charAt(0).toUpperCase()}${subject.slice(1)}`.slice(0, 120) : undefined;
}

function uniqueCardId(title: string, existingIds: Array<string>): string {
  const base =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .replace(/^([^a-z])/, 'card-$1')
      .slice(0, 58) || 'card';
  const ids = new Set(existingIds);
  if (!ids.has(base)) {
    return base;
  }
  let suffix = 2;
  while (ids.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}
